import React, { useRef, useEffect, useState, useCallback } from 'react';

const AgColorPicker = ({ color = '#ff0000', onChange }) => {
    const colorMapRef = useRef(null);
    const hueSliderRef = useRef(null);

    const [hue, setHue] = useState(0); // 0 to 360
    const [saturation, setSaturation] = useState(1); // 0 to 1
    const [value, setValue] = useState(1); // 0 to 1

    const [isDraggingMap, setIsDraggingMap] = useState(false);
    const [isDraggingHue, setIsDraggingHue] = useState(false);
    const [hexInput, setHexInput] = useState((color || '#ff0000').replace('#', '').toUpperCase());

    // Convert Hex to HSV on mount or when external color changes significantly
    useEffect(() => {
        if (!color) return;
        const hex = color.replace('#', '');
        if (hex.length === 6) {
            const r = parseInt(hex.substring(0, 2), 16) / 255;
            const g = parseInt(hex.substring(2, 4), 16) / 255;
            const b = parseInt(hex.substring(4, 6), 16) / 255;

            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h, s, v = max;
            const d = max - min;
            s = max === 0 ? 0 : d / max;

            if (max === min) {
                h = 0; // achromatic
            } else {
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                    default: h = 0;
                }
                h /= 6;
            }

            // Only set initial state if not currently dragging to avoid jumpiness
            if (!isDraggingMap && !isDraggingHue) {
                setHue(h * 360);
                setSaturation(s);
                setValue(v);
                setHexInput(hex.toUpperCase());
            }
        }
    }, [color]);

    // HSV to RGB helper
    const hsvToRgb = (h, s, v) => {
        let r, g, b;
        let i = Math.floor(h * 6);
        let f = h * 6 - i;
        let p = v * (1 - s);
        let q = v * (1 - f * s);
        let t = v * (1 - (1 - f) * s);

        switch (i % 6) {
            case 0: r = v; g = t; b = p; break;
            case 1: r = q; g = v; b = p; break;
            case 2: r = p; g = v; b = t; break;
            case 3: r = p; g = q; b = v; break;
            case 4: r = t; g = p; b = v; break;
            case 5: r = v; g = p; b = q; break;
            default: r = 0; g = 0; b = 0;
        }

        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    };

    const rgbToHex = (r, g, b) => {
        return "#" + (1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1).toUpperCase();
    };

    // Draw Color Map (Saturation / Value)
    useEffect(() => {
        const canvas = colorMapRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Fill base hue color
        ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
        ctx.fillRect(0, 0, width, height);

        // White gradient from left to right
        const whiteGrad = ctx.createLinearGradient(0, 0, width, 0);
        whiteGrad.addColorStop(0, 'rgba(255,255,255,1)');
        whiteGrad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = whiteGrad;
        ctx.fillRect(0, 0, width, height);

        // Black gradient from bottom to top
        const blackGrad = ctx.createLinearGradient(0, height, 0, 0);
        blackGrad.addColorStop(0, 'rgba(0,0,0,1)');
        blackGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = blackGrad;
        ctx.fillRect(0, 0, width, height);

        // Draw picker circle
        const x = saturation * width;
        const y = (1 - value) * height;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, 2 * Math.PI);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.stroke();
    }, [hue, saturation, value]);

    // Draw Hue Slider
    useEffect(() => {
        const canvas = hueSliderRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Create a smooth hue gradient from top to bottom
        const grad = ctx.createLinearGradient(0, 0, 0, height);
        grad.addColorStop(0, '#ff0000');    // 360 / 0 = Red
        grad.addColorStop(0.17, '#ffff00'); // 60 = Yellow
        grad.addColorStop(0.33, '#00ff00'); // 120 = Green
        grad.addColorStop(0.5, '#00ffff');  // 180 = Cyan
        grad.addColorStop(0.67, '#0000ff'); // 240 = Blue
        grad.addColorStop(0.83, '#ff00ff'); // 300 = Magenta
        grad.addColorStop(1, '#ff0000');    // Red
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        // Draw hue selector line
        const y = (hue / 360) * height;
        ctx.beginPath();
        ctx.rect(0, y - 2, width, 4);
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.stroke();
    }, [hue]);

    const updateColor = useCallback((h, s, v) => {
        const [r, g, b] = hsvToRgb(h / 360, s, v);
        const hex = rgbToHex(r, g, b);
        setHexInput(hex.replace('#', ''));
        if (onChange) onChange(hex);
    }, [onChange]);

    const handleHexInputChange = (e) => {
        const val = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6).toUpperCase();
        setHexInput(val);
        if (val.length === 6) {
            const hexColor = '#' + val;
            const r = parseInt(val.substring(0, 2), 16) / 255;
            const g = parseInt(val.substring(2, 4), 16) / 255;
            const b = parseInt(val.substring(4, 6), 16) / 255;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h, s, v2 = max;
            const d = max - min;
            s = max === 0 ? 0 : d / max;
            if (max === min) { h = 0; }
            else {
                switch (max) {
                    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                    case g: h = (b - r) / d + 2; break;
                    case b: h = (r - g) / d + 4; break;
                    default: h = 0;
                }
                h /= 6;
            }
            setHue(h * 360);
            setSaturation(s);
            setValue(v2);
            if (onChange) onChange(hexColor);
        }
    };

    const handleMapInteraction = (e) => {
        const canvas = colorMapRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;

        x = Math.max(0, Math.min(x, rect.width));
        y = Math.max(0, Math.min(y, rect.height));

        const s = x / rect.width;
        const v = 1 - (y / rect.height);

        setSaturation(s);
        setValue(v);
        updateColor(hue, s, v);
    };

    const handleHueInteraction = (e) => {
        const canvas = hueSliderRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        let y = e.clientY - rect.top;
        y = Math.max(0, Math.min(y, rect.height));

        const h = (y / rect.height) * 360;
        setHue(h);
        updateColor(h, saturation, value);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDraggingMap) handleMapInteraction(e);
            if (isDraggingHue) handleHueInteraction(e);
        };

        const handleMouseUp = () => {
            setIsDraggingMap(false);
            setIsDraggingHue(false);
        };

        if (isDraggingMap || isDraggingHue) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingMap, isDraggingHue, hue, saturation, value, updateColor]);

    return (
        <div className="flex flex-col gap-2 p-3 bg-[#1e1e1e] rounded-md border border-[#333] shadow-lg w-[280px]">
            <div className="flex gap-2">
                <canvas
                    ref={colorMapRef}
                    width={220}
                    height={120}
                    className="rounded cursor-crosshair border border-gray-900 w-full"
                    onMouseDown={(e) => {
                        setIsDraggingMap(true);
                        handleMapInteraction(e);
                    }}
                />
                <canvas
                    ref={hueSliderRef}
                    width={20}
                    height={120}
                    className="rounded cursor-ns-resize border border-gray-900 mx-1 flex-shrink-0"
                    onMouseDown={(e) => {
                        setIsDraggingHue(true);
                        handleHueInteraction(e);
                    }}
                />
            </div>
            <div className="flex items-center gap-2">
                <div
                    className="w-6 h-6 rounded border border-gray-600 flex-shrink-0"
                    style={{ backgroundColor: `#${hexInput.padEnd(6, '0')}` }}
                />
                <div className="flex items-center bg-[#2d2d2d] rounded h-7 flex-1 border border-transparent focus-within:border-blue-500">
                    <span className="text-gray-400 text-xs pl-2 select-none">#</span>
                    <input
                        type="text"
                        value={hexInput}
                        onChange={handleHexInputChange}
                        maxLength={6}
                        className="w-full h-full bg-transparent text-white text-xs px-1 border-none focus:outline-none focus:ring-0 uppercase tracking-wider font-mono"
                        placeholder="FF0000"
                    />
                </div>
            </div>
        </div>
    );
};

export default AgColorPicker;
